import { useCallback } from "react";
import type { ToolUsage, ToolUsageRange } from "../lib/toolUsage";
import { useCachedQuery, type UseCachedQueryResult } from "./useCachedQuery";

const viewerZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "UTC";
  }
};

const NO_INSTANCE: ToolUsage = {
  range: "30d",
  bucketSeconds: 86_400,
  buckets: [],
  apps: [],
  instanceLimits: [],
  appLimits: [],
  enforced: { instance: false, perApp: false },
};

export const toolUsageUrl = (tenantId: string, range: ToolUsageRange): string =>
  `/api/claws/${encodeURIComponent(tenantId)}/tool-usage?range=${range}&tz=${encodeURIComponent(viewerZone())}`;

// The 30-day view is the default (overview cell + first paint of the usage
// page), so it is the one that gets a warm cache.
export const useToolUsage = (
  tenantId: string | null,
  range: ToolUsageRange = "30d",
): UseCachedQueryResult<ToolUsage> => {
  const url = tenantId === null ? "" : toolUsageUrl(tenantId, range);
  const fetcher = useCallback(
    async (signal: AbortSignal): Promise<ToolUsage> => {
      if (url === "") return NO_INSTANCE;
      const res = await fetch(url, { credentials: "include", signal });
      if (!res.ok) throw new Error(`tool usage: HTTP ${res.status}`);
      return (await res.json()) as ToolUsage;
    },
    [url],
  );
  return useCachedQuery<ToolUsage>({
    cacheResource: tenantId !== null && range === "30d" ? `tool-usage:${tenantId}` : null,
    queryKey: url,
    fetcher,
    refreshIntervalMs: 60_000,
  });
};
